import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule, NavigationEnd } from '@angular/router';
import { YardiePicksTabsService, CollectionTab } from '../../core/services/yardie-picks-tabs.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

const DEFAULT_COLLECTION = 'toppatop';

@Component({
  standalone: true,
  selector: 'app-yardie-picks-page',
  imports: [
    CommonModule,
    RouterModule
  ],
  templateUrl: './yardie-picks-page.component.html',
  styleUrl: './yardie-picks-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YardiePicksPageComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Collection tabs from service
  collectionTabs = signal<CollectionTab[]>([]);

  // Current active tab based on route
  activeTab = signal<string>('');

  // Computed for active tab details
  activeTabDetails = computed(() => {
    const tabs = this.collectionTabs();
    const active = this.activeTab();
    return tabs.find(tab => tab.collectionName.toLowerCase() === active.toLowerCase());
  });

  constructor(
    private tabsService: YardiePicksTabsService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.collectionTabs.set(this.tabsService.getTabs());

    // Subscribe to route changes
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateActiveTab();
      });

    // Also listen to navigation end for completeness
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateActiveTab();
      });

    // Initial update
    this.updateActiveTab();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateActiveTab(): void {
    // Get the current route segments
    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children['primary']?.segments || [];

    if (segments.length >= 2) {
      // For /picks/toppatop, segments would be ['picks', 'toppatop']
      const lastSegment = segments[segments.length - 1].path;
      const matchingTab = this.collectionTabs().find(
        tab => tab.collectionName.toLowerCase() === lastSegment.toLowerCase()
      );
      if (matchingTab && this.activeTab() !== lastSegment.toLowerCase()) {
        this.activeTab.set(lastSegment.toLowerCase());
      } else if (!matchingTab) {
        this.activeTab.set(DEFAULT_COLLECTION);
      }
    } else {
      this.activeTab.set(DEFAULT_COLLECTION);
    }
  }

  // Navigate to a specific collection tab
  navigateToTab(collectionName: string): void {
    const routeName = collectionName.toLowerCase();
    this.activeTab.set(routeName); // Set immediately for responsive UI
    this.router.navigate([routeName], { relativeTo: this.route });
  }

  // Check if tab is active
  isActive(collectionName: string): boolean {
    return this.activeTab() === collectionName.toLowerCase();
  }

  // TrackBy function for ngFor performance
  trackByCollectionName(index: number, tab: CollectionTab): string {
    return tab.collectionName;
  }
}
